import { useState, useCallback, useMemo } from 'react';
import { AskRequest } from '@hone-automation/common';
import type { WorkState } from '../components/RequestCard';
import type { DueDatePreset, DateRange, RequestTypeFilter } from '../components/BrowserToolbar/components/FilterModal';
import { requestTypeMapping } from '../components/BrowserToolbar/components/FilterModal';
import { getDateForField, parseLocalDate, type DateField } from '../utils/dateGrouping';
import { filterBySearch, getTextQuery } from '../utils/searchFilter';

export interface FilterCounts {
  mentionCount: number;
  attachmentCount: number;
  assignedCount: number;
  groupCount: number;
  workStateCounts: Record<WorkState, number>;
  requestTypeCounts: Record<RequestTypeFilter, number>;
}

export interface ToolbarFilterState {
  mentionFilterActive: boolean;
  attachmentFilterActive: boolean;
  assignedFilterActive: boolean;
  groupFilterActive: boolean;
  workStateFilter: Set<WorkState>;
  dueDateFilter: DueDatePreset;
  dateRangeFilter: DateRange;
  requestTypeFilter: Set<RequestTypeFilter>;
  assigneeFilter: string | null;
  searchQuery: string;
}

export interface UseToolbarFiltersOptions {
  /** Requests filtered by the main view (before toolbar filters) */
  viewFilteredRequests: AskRequest[];
  /** Current user ID for mention detection */
  userId: string | null | undefined;
  /** Date field used for date range filtering */
  dateField: DateField;
}

export interface UseToolbarFiltersReturn {
  // Filter state
  filters: ToolbarFilterState;
  // Setters
  setMentionFilterActive: (active: boolean) => void;
  setAttachmentFilterActive: (active: boolean) => void;
  setAssignedFilterActive: (active: boolean) => void;
  setGroupFilterActive: (active: boolean) => void;
  setWorkStateFilter: React.Dispatch<React.SetStateAction<Set<WorkState>>>;
  setDueDateFilter: (preset: DueDatePreset) => void;
  setDateRangeFilter: (range: DateRange) => void;
  setRequestTypeFilter: React.Dispatch<React.SetStateAction<Set<RequestTypeFilter>>>;
  setAssigneeFilter: (assigneeId: string | null) => void;
  setSearchQuery: (query: string) => void;
  // Reset all toolbar filters
  resetFilters: () => void;
  // Check functions (for use in rendering)
  checkHasMention: (request: AskRequest) => boolean;
  checkHasAttachment: (request: AskRequest) => boolean;
  checkHasAssignee: (request: AskRequest) => boolean;
  checkGetWorkState: (request: AskRequest) => WorkState;
  // Filter function (apply current filters to any request array)
  // Pass { skipGroupFilter: true } for children inside groups
  filterRequests: (requests: AskRequest[], options?: { skipGroupFilter?: boolean }) => AskRequest[];
  // Computed values
  filterCounts: FilterCounts;
  toolbarFilteredRequests: AskRequest[];
  highlightQuery: string;
}

/**
 * Hook for managing toolbar filter state and filtering logic
 */
export const useToolbarFilters = ({
  viewFilteredRequests,
  userId,
  dateField,
}: UseToolbarFiltersOptions): UseToolbarFiltersReturn => {
  // Filter state
  const [mentionFilterActive, setMentionFilterActive] = useState(false);
  const [attachmentFilterActive, setAttachmentFilterActive] = useState(false);
  const [assignedFilterActive, setAssignedFilterActive] = useState(false);
  const [groupFilterActive, setGroupFilterActive] = useState(false);
  const [workStateFilter, setWorkStateFilter] = useState<Set<WorkState>>(new Set());
  const [dueDateFilter, setDueDateFilter] = useState<DueDatePreset>(null);
  const [dateRangeFilter, setDateRangeFilter] = useState<DateRange>({ from: null, to: null });
  const [requestTypeFilter, setRequestTypeFilter] = useState<Set<RequestTypeFilter>>(new Set());
  const [assigneeFilter, setAssigneeFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Extract text portion of search query for highlighting
  const highlightQuery = useMemo(() => getTextQuery(searchQuery), [searchQuery]);

  // Reset all toolbar filters
  const resetFilters = useCallback(() => {
    setMentionFilterActive(false);
    setAttachmentFilterActive(false);
    setAssignedFilterActive(false);
    setGroupFilterActive(false);
    setWorkStateFilter(new Set());
    setDueDateFilter(null);
    setDateRangeFilter({ from: null, to: null });
    setRequestTypeFilter(new Set());
    setAssigneeFilter(null);
    setSearchQuery('');
  }, []);

  // Check functions
  const checkHasMention = useCallback(
    (request: AskRequest): boolean => {
      if (!userId || !request.comments) return false;
      return request.comments.some((comment: any) => {
        const commentText = comment?.comment || '';
        return commentText.includes(`::${userId}::`);
      });
    },
    [userId]
  );

  const checkHasAttachment = useCallback((request: AskRequest): boolean => {
    if (!request.comments) return false;
    return request.comments.some((comment: any) => {
      return comment?.attachments && comment.attachments.length > 0;
    });
  }, []);

  const checkHasAssignee = useCallback((request: AskRequest): boolean => {
    return !!request.assignee;
  }, []);

  const checkIsGroup = useCallback((request: AskRequest): boolean => {
    return request.type === 'Group';
  }, []);

  const checkGetWorkState = useCallback((request: AskRequest): WorkState => {
    // @ts-ignore - priority field exists but may not be typed
    if (request.priority === 'Blocker') {
      return 'blocked';
    }
    if (request.status === 'Closed') {
      return 'closed';
    }
    if (request.comments && request.comments.length > 0) {
      const uniqueAuthors = new Set<string>();
      request.comments.forEach((comment: any) => {
        const authorId = comment?.userLocation?.userId || comment?.userLocation?.email || comment?.user?.id;
        if (authorId) {
          uniqueAuthors.add(authorId);
        }
      });
      if (uniqueAuthors.size >= 2) {
        return 'in-progress';
      }
    }
    return 'todo';
  }, []);

  // Check if request matches due date filter
  const checkMatchesDueDate = useCallback((request: AskRequest, preset: DueDatePreset): boolean => {
    if (preset === null) return true;

    // @ts-ignore - dueDate field may exist
    const dueDate = request.dueDate ? parseLocalDate(request.dueDate) : null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (preset) {
      case 'no-due-date':
        return dueDate === null;
      case 'overdue':
        return dueDate !== null && dueDate < today;
      case 'today': {
        if (!dueDate) return false;
        const dueDateNorm = new Date(dueDate);
        dueDateNorm.setHours(0, 0, 0, 0);
        return dueDateNorm.getTime() === today.getTime();
      }
      case 'this-week': {
        if (!dueDate) return false;
        const dueDateNorm = new Date(dueDate);
        dueDateNorm.setHours(0, 0, 0, 0);
        const endOfWeek = new Date(today);
        endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
        endOfWeek.setHours(23, 59, 59, 999);
        return dueDateNorm >= today && dueDateNorm <= endOfWeek;
      }
      case 'this-month': {
        if (!dueDate) return false;
        const dueDateNorm = new Date(dueDate);
        dueDateNorm.setHours(0, 0, 0, 0);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        endOfMonth.setHours(23, 59, 59, 999);
        return dueDateNorm >= today && dueDateNorm <= endOfMonth;
      }
      default:
        return true;
    }
  }, []);

  // Check if request matches date range filter
  const checkMatchesDateRange = useCallback(
    (request: AskRequest, range: DateRange): boolean => {
      if (range.from === null && range.to === null) return true;

      const requestDate = getDateForField(request, dateField);
      if (!requestDate) return false;

      const reqDateNorm = new Date(requestDate);
      reqDateNorm.setHours(0, 0, 0, 0);

      if (range.from !== null) {
        const fromNorm = new Date(range.from);
        fromNorm.setHours(0, 0, 0, 0);
        if (reqDateNorm < fromNorm) return false;
      }

      if (range.to !== null) {
        const toNorm = new Date(range.to);
        toNorm.setHours(23, 59, 59, 999);
        if (reqDateNorm > toNorm) return false;
      }

      return true;
    },
    [dateField]
  );

  // Check if request matches request type filter
  const checkMatchesRequestType = useCallback((request: AskRequest, types: Set<RequestTypeFilter>): boolean => {
    if (types.size === 0) return true;

    // @ts-ignore - type field exists on request
    const requestType = request.type;
    if (!requestType) return false;

    for (const filterType of types) {
      const allowedTypes = requestTypeMapping[filterType];
      if (allowedTypes.includes(requestType)) {
        return true;
      }
    }

    return false;
  }, []);

  // Calculate counts BEFORE applying toolbar filters
  const filterCounts = useMemo((): FilterCounts => {
    let mentionCount = 0;
    let attachmentCount = 0;
    let assignedCount = 0;
    const groupIdsWithChildren = new Set<string>();
    const workStateCounts: Record<WorkState, number> = {
      backlog: 0,
      todo: 0,
      'in-progress': 0,
      blocked: 0,
      closed: 0,
    };
    const requestTypeCounts: Record<RequestTypeFilter, number> = {
      email: 0,
      transaction: 0,
      'flagged-cell': 0,
      'custom-request': 0,
    };

    viewFilteredRequests.forEach(request => {
      if (checkHasMention(request)) mentionCount++;
      if (checkHasAttachment(request)) attachmentCount++;
      if (checkHasAssignee(request)) assignedCount++;
      // Track groups that have children (real groups via type, synthetic via parentId)
      if (request.parentId) groupIdsWithChildren.add(request.parentId);
      const ws = checkGetWorkState(request);
      workStateCounts[ws]++;

      // @ts-ignore - type field exists on request
      const requestType = request.type;
      if (requestType) {
        if (requestType === 'Email') requestTypeCounts.email++;
        else if (requestType === 'Ask Bookkeeper' || requestType === 'Ask Customer') requestTypeCounts.transaction++;
        else if (requestType === 'Flagged Cell') requestTypeCounts['flagged-cell']++;
        else if (requestType === 'Customer Request') requestTypeCounts['custom-request']++;
      }
    });

    const groupCount = groupIdsWithChildren.size;

    return { mentionCount, attachmentCount, assignedCount, groupCount, workStateCounts, requestTypeCounts };
  }, [viewFilteredRequests, checkHasMention, checkHasAttachment, checkHasAssignee, checkGetWorkState]);

  // Reusable filter function that applies all current toolbar filters to any request array
  // Options:
  //   skipGroupFilter: skip the groupFilterActive check (for filtering children inside groups)
  const filterRequests = useCallback(
    (requests: AskRequest[], options?: { skipGroupFilter?: boolean }): AskRequest[] => {
      let result = requests;

      // Apply mention/attachment filter (OR logic)
      if (mentionFilterActive || attachmentFilterActive) {
        result = result.filter(request => {
          const hasMentionMatch = mentionFilterActive && checkHasMention(request);
          const hasAttachmentMatch = attachmentFilterActive && checkHasAttachment(request);
          return hasMentionMatch || hasAttachmentMatch;
        });
      }

      // Apply assigned filter
      if (assignedFilterActive) {
        result = result.filter(request => checkHasAssignee(request));
      }

      // Apply work state filter
      if (workStateFilter.size > 0) {
        result = result.filter(request => {
          const requestWorkState = checkGetWorkState(request);
          return workStateFilter.has(requestWorkState);
        });
      }

      // Apply due date filter
      if (dueDateFilter !== null) {
        result = result.filter(request => checkMatchesDueDate(request, dueDateFilter));
      }

      // Apply date range filter
      if (dateRangeFilter.from !== null || dateRangeFilter.to !== null) {
        result = result.filter(request => checkMatchesDateRange(request, dateRangeFilter));
      }

      // Apply request type filter
      if (requestTypeFilter.size > 0) {
        result = result.filter(request => checkMatchesRequestType(request, requestTypeFilter));
      }

      // Apply assignee filter
      if (assigneeFilter !== null) {
        result = result.filter(request => request.assignee === assigneeFilter);
      }

      // Apply group filter (show groups and their children) - skip for children inside groups
      if (groupFilterActive && !options?.skipGroupFilter) {
        result = result.filter(request => checkIsGroup(request) || request.parentId);
      }

      // Apply search filter
      if (searchQuery.trim()) {
        result = filterBySearch(result, searchQuery);
      }

      return result;
    },
    [
      mentionFilterActive,
      attachmentFilterActive,
      assignedFilterActive,
      groupFilterActive,
      workStateFilter,
      dueDateFilter,
      dateRangeFilter,
      requestTypeFilter,
      assigneeFilter,
      searchQuery,
      checkHasMention,
      checkHasAttachment,
      checkHasAssignee,
      checkIsGroup,
      checkGetWorkState,
      checkMatchesDueDate,
      checkMatchesDateRange,
      checkMatchesRequestType,
    ]
  );

  // Apply toolbar filters to get final filtered requests
  const toolbarFilteredRequests = useMemo(
    () => filterRequests(viewFilteredRequests),
    [filterRequests, viewFilteredRequests]
  );

  return {
    filters: {
      mentionFilterActive,
      attachmentFilterActive,
      assignedFilterActive,
      groupFilterActive,
      workStateFilter,
      dueDateFilter,
      dateRangeFilter,
      requestTypeFilter,
      assigneeFilter,
      searchQuery,
    },
    setMentionFilterActive,
    setAttachmentFilterActive,
    setAssignedFilterActive,
    setGroupFilterActive,
    setWorkStateFilter,
    setDueDateFilter,
    setDateRangeFilter,
    setRequestTypeFilter,
    setAssigneeFilter,
    setSearchQuery,
    resetFilters,
    checkHasMention,
    checkHasAttachment,
    checkHasAssignee,
    checkGetWorkState,
    filterRequests,
    filterCounts,
    toolbarFilteredRequests,
    highlightQuery,
  };
};
